import { ArrowRight, Banknote, Eye, TrendingUp } from 'lucide-react';
import React from 'react';

import usePage from '@dashboard/hooks/usePage';

import getConversionRate from '@dashboard/services/getConversionRate';

import CardHeaderTitle from '@dashboard/components/CardHeaderTitle';
import Link from '@dashboard/components/Link';

import * as KPICard from '../components/KPICard';
import RecentSubmissions from '../components/RecentSubmissions';
import PageOverviewHeader from '../components/PageOverviewHeader';
import { KPITrend, TopReferrers } from '../components/PageCharts';
import { DetailBaseContext } from '@dashboard/lib/detailLinks';
import Spinner from '@dashboard/components/Spinner';
import { usePageSummary } from '../components/PageSummaryRange';

export default function CheckoutOverview() {
  const { page } = usePage();

  const { summary, isLoading } = usePageSummary();
  const totals = summary?.totals;
  const conversionRate = getConversionRate(totals?.conversions, totals?.views);

  return (
    <div>
      <PageOverviewHeader
        page={page}
        side={
          <>
            <KPICard.Container>
              <KPICard.Icon icon={TrendingUp} />
              <KPICard.Header>
                <KPICard.Title>Conversion rate</KPICard.Title>
                <KPICard.InfoTooltip>
                  Submissions ÷ page views. Counts submissions you later canceled.
                </KPICard.InfoTooltip>
              </KPICard.Header>
              <KPICard.Value>
                {isLoading ? <Spinner size="small" /> : `${conversionRate}%`}
              </KPICard.Value>
            </KPICard.Container>
            <TopReferrers />
          </>
        }
      >
        <KPICard.Container>
          <KPICard.Icon icon={Banknote} />
          <KPICard.Header>
            <KPICard.Title>Submissions</KPICard.Title>
            <KPICard.InfoTooltip>
              Number of succeeded submissions. Not including canceled submissions.
            </KPICard.InfoTooltip>
          </KPICard.Header>
          <KPICard.Value>
            {isLoading ? <Spinner size="small" /> : (totals?.submissions ?? 0)}
          </KPICard.Value>
          <KPITrend metric="submissions" label="Submissions" />
        </KPICard.Container>

        <KPICard.Container>
          <KPICard.Icon icon={Eye} />
          <KPICard.Header>
            <KPICard.Title>Page views</KPICard.Title>
            <KPICard.InfoTooltip>Total number of form views.</KPICard.InfoTooltip>
          </KPICard.Header>
          <KPICard.Value>
            {isLoading ? <Spinner size="small" /> : (totals?.views ?? 0)}
          </KPICard.Value>
          <KPITrend metric="views" label="Page views" />
        </KPICard.Container>
      </PageOverviewHeader>

      <div className="mt-8">
        <div className="flex justify-between items-center mb-4">
          <CardHeaderTitle variant="root">Recent submissions</CardHeaderTitle>

          <div>
            <Link
              href={`/pages/${page._id}/submissions`}
              className="flex items-center gap-x-1 whitespace-nowrap text-sm"
            >
              View all <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>

        <DetailBaseContext.Provider value={`/pages/${page._id}/submissions`}>
          <RecentSubmissions />
        </DetailBaseContext.Provider>
      </div>
    </div>
  );
}
